import type {
  InputLabelProps,
  TextFieldProps as MuiTextFieldProps,
  PopperPlacementType,
  TooltipProps,
} from "@mui/material";
import {
  InputLabel,
  TextField as MuiTextField,
  Typography,
  mergeSlotProps,
} from "@mui/material";
import { useFormControl } from "@mui/material/FormControl";
import type { ReactNode } from "react";
import { useId, useMemo, useRef } from "react";

import { ErrorOutlineIcon } from "../icons";
import { Tooltip } from "../tooltip";

import * as sx from "./text-field.sx";

export interface TextFieldProps extends Omit<
  MuiTextFieldProps,
  "error" | "label" | "InputLabelProps"
> {
  error?: string;
  /** A tip in a floating box beside the field. Set it only while the field has focus. */
  hint?: ReactNode;
  hintPlacement?: PopperPlacementType;
  label?: string;
  tooltipContent?: TooltipProps["title"];
  /** Exposed so that the Combobox can hide the Tooltip when the dropdown is open */
  tooltipOpen?: boolean;
  tooltipPlacement?: TooltipProps["placement"];
}

function TextFieldInputLabel({ shrink, ...props }: InputLabelProps) {
  const formControl = useFormControl();
  return (
    <InputLabel
      {...props}
      shrink={
        // this is the MUI logic, except do not force shrink when start adorned
        shrink ?? Boolean(formControl?.filled || formControl?.focused)
      }
    />
  );
}

const slots = {
  inputLabel: TextFieldInputLabel,
} satisfies TextFieldProps["slots"];

export function TextField({
  error,
  hint,
  hintPlacement = "top-start",
  id: idProp,
  onBlur,
  placeholder,
  slotProps,
  tooltipOpen,
  tooltipContent,
  tooltipPlacement,
  ...props
}: TextFieldProps) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const hintId = hint ? `${id}-hint` : undefined;
  // htmlInput props override the aria-describedby Material UI sets for
  // helperText, so reference both the helper text and the floating hint here
  const describedBy =
    [props.helperText ? `${id}-helper-text` : undefined, hintId]
      .filter(Boolean)
      .join(" ") || undefined;

  // Material UI always shrinks the label if there's a start adornment present.
  // To get it to shrink only when focused, we have to manage the state manually.
  const slotPropsMod = useMemo(() => {
    const errorAdornment = error ? (
      <ErrorOutlineIcon fontSize="small" />
    ) : undefined;

    return {
      ...slotProps,
      htmlInput: mergeSlotProps(
        {
          "aria-describedby": describedBy,
          "aria-errormessage": error,
          // disable browser validation
          required: false,
        },
        slotProps?.htmlInput ?? {},
      ),
      input: mergeSlotProps(
        {
          // need to set notched to false to work with custom shrink below
          notched: ["outlined", undefined].includes(props.variant)
            ? false
            : undefined,
          ...(errorAdornment ? { startAdornment: errorAdornment } : {}),
        },
        slotProps?.input ?? {},
      ),
      inputLabel: mergeSlotProps(slotProps?.inputLabel ?? {}, {
        // force shrink when placeholder exists, but otherwise defer to InputLabel
        shrink: placeholder ? true : undefined,
      }),
    };
  }, [describedBy, error, placeholder, props.variant, slotProps]);

  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <Tooltip
        error={error}
        open={tooltipOpen}
        placement={tooltipPlacement}
        title={tooltipContent}
      >
        <MuiTextField
          {...props}
          inputRef={(ref) => {
            inputRef.current = ref;
            if (ref) {
              /**
               * The standard React blur handling occasionally fails to fire.
               *
               * My theory is that this is caused by React hoisting all event
               * handlers up to the DOM root.
               *
               * Specifically consider the case of a drag-and-drop card where the
               * entirety of the card is a target for grabbing. An input field
               * within the card should have its own focus/blur events. However,
               * because focus/blur events are handled at the root and managed via
               * React, the system may be confused as to if a blur has actually
               * happened based on switching between the input field and its
               * drag-and-drop container.
               *
               * At any rate, attaching the blur handler directly to the input field
               * via DOM manipulation fixes this issue.
               */
              ref.onblur = onBlur;
            }
            // standard implementation of setting ref value
            if (props.inputRef) {
              if (typeof props.inputRef === "function") {
                props.inputRef(ref);
              } else {
                props.inputRef.current = ref;
              }
            }
          }}
          error={Boolean(error)}
          id={id}
          placeholder={placeholder}
          slotProps={slotPropsMod}
          slots={slots}
        />
      </Tooltip>
      <sx.HintPopper
        anchorEl={inputRef.current?.parentElement?.parentElement}
        placement={hintPlacement}
        open={Boolean(hint)}
      >
        <sx.HintPaper elevation={4}>
          <Typography id={hintId}>{hint}</Typography>
        </sx.HintPaper>
      </sx.HintPopper>
    </>
  );
}
